import * as GameHistoryActions from './gameHistory.actions';
import { INIT_GAME_HISTORY_STATE, IHandHistories, GameHistory, INIT_GAME_HISTORY, HandDetails, HandDetailsActions, DetailsActionType, DetailsActionPlayer, PlayerCard, INITIAL_HAND_DETAILS_ACTIONS, ShowdownDetails, GameHistoryState, MAX_DISPLAYABLE_HISTORY_COUNT } from './gameHistory.state';
import { IGameSummary } from '@soposopo/sopo-models';
import { Action, createReducer, on } from '@ngrx/store';
import { GameStateId } from '../game/game.state';

const _gameHistoryReducer = createReducer(

    INIT_GAME_HISTORY_STATE,

    on(
        GameHistoryActions.addNewHistoryAction,
        (state, action) => {
            const gameHistory = {...state.gameHistories};

            if(!gameHistory[action.gameStateId]) {
                gameHistory[action.gameStateId] = {...INIT_GAME_HISTORY};
            }

            return {
                ...state,
                gameHistories: gameHistory
            };
        }
    ),

    on(
        GameHistoryActions.clearUnusedHistoriesAction,
        (state, action) => {
            const gameHistory: {[key: string]:  GameHistory } = {};
            action.remainingGames.forEach(game => {
                gameHistory[game] = {...state.gameHistories[game]};
            })
            return {
                ...state,
                gameHistories: gameHistory
            };
        }
    ),

    on(
        GameHistoryActions.displayDetails,
        (state, action) => {
            const gameHistory = {...state.gameHistories};
            gameHistory[action.gameStateId].activeTab = 'Details';
            return {
                ...state,
                gameHistories: gameHistory
            };
        }
    ),

    on(
        GameHistoryActions.displaySummary,
        (state, action) => {
            const gameHistory = {...state.gameHistories};
            gameHistory[action.gameStateId].activeTab = 'Summary';
            return {
                ...state,
                gameHistories: gameHistory
            };
        }
    ),

    on(
        GameHistoryActions.selectNextHandHistoryAction,
        (state, action) => {
            const gameHistory = {...state.gameHistories};
            gameHistory[action.gameStateId].historyIndex = nextHandHistory({...state.gameHistories[action.gameStateId]});
            return {
                ...state,
                gameHistories: gameHistory
            };
        }
    ),

    on(
        GameHistoryActions.selectPreviousHandHistoryAction,
        (state, action) => {
            const gameHistory = {...state.gameHistories};
            gameHistory[action.gameStateId].historyIndex = previousHandHistory({...state.gameHistories[action.gameStateId]});
            return {
                ...state,
                gameHistories: gameHistory
            };
        }
    ),

    on(
        GameHistoryActions.receiveGameSummary,
        (state, action) => {
            const gameHistory = {...state.gameHistories};
            gameHistory[action.gameStateId].gameHistories = addGameHistory
            (
               { ...state.gameHistories[action.gameStateId].gameHistories},
                action.summary,
                action.events,
                action.handId,
                action.tableId,
                state.gameHistories[action.gameStateId].historyIDs,
            );
            gameHistory[action.gameStateId].historyIndex = setHandHistoryIndex(state.gameHistories[action.gameStateId].historyIndex);
            gameHistory[action.gameStateId].historyIDs = addHandID(state.gameHistories[action.gameStateId].historyIDs.slice(), action.handId);
            return {
                ...state,
                gameHistories: gameHistory
            };
        }
    ),

    on(
        GameHistoryActions.resetHandHistoryCounter,
        (state, action) => {
            const gameHistory = { ...state.gameHistories };
            if(Object.keys(gameHistory[action.gameStateId].gameHistories).length > 0)
            gameHistory[action.gameStateId].historyIndex =
                setHandHistoryIndex(-1)
            return {
                ...state,
                gameHistories: gameHistory
            }
        }
    )
)

function previousHandHistory(state: GameHistory): number {
    return state.historyIndex > 0 ? state.historyIndex - 1 : state.historyIndex;
}

function nextHandHistory(state: GameHistory) {
    return state.historyIndex < state.historyIDs.length && state.historyIndex !== -1
        ? state.historyIndex + 1
        : state.historyIndex;
}

function setHandHistoryIndex(index: number): number {
    if (index === -1) {
        return 0;
    } else if (index === MAX_DISPLAYABLE_HISTORY_COUNT - 1) {
        return index
    } else {
        return index + 1;
    }
}

function addGameHistory(
    summaries: IHandHistories,
    summary: IGameSummary,
    events: any[],
    handID: string,
    tableID: string,
    historyIDs: string[],
): IHandHistories {
    const newSummaries = { ...summaries };
    const handIDs = Object.assign([], historyIDs);
    const gameHistorySummary: any = {};
    if (handIDs.indexOf(handID) < 0) {
        handIDs.push(handID);
    }
    const newHandIDs = handIDs.slice(Math.max(handIDs.length - MAX_DISPLAYABLE_HISTORY_COUNT, 0));
    const communityCardsArray: any[][] = [];
    const playersFilteredArray: any[] = [];
    const isMultiple = summary['runMultipleWinnerObjects'] ? summary['runMultipleWinnerObjects'].length > 0 : false;
    if (isMultiple) {
        if(summary['runMultipleWinnerObjects']) {
            summary['runMultipleWinnerObjects'].forEach(winnerObj => {
                const playersFiltered = {};
                Object.keys(winnerObj.players).forEach(playerId => {
                    const playerStripped = {};
                    playerStripped['playerID'] = playerId;
                    playerStripped['playerName'] = winnerObj.players[playerId].name;
                    playerStripped['profit'] = winnerObj.winnerObj[playerId][0];
                    playerStripped['hand'] = winnerObj.players[playerId]['hand'];
                    playerStripped['folded'] = winnerObj.players[playerId]['foldedRound'];
                    playerStripped['shortHandDescription'] = winnerObj.players[playerId]['shortHandDescription'];
                    playerStripped['pokerExpressionsDescriptions'] = summary.players[playerId]['pokerExpressionsDescriptions'];
                    playerStripped['bestCards'] = winnerObj.players[playerId]['bestCards'];
                    playerStripped['handDescription'] = winnerObj.players[playerId]['handDescription'];
                    playersFiltered[playerId] = playerStripped;
                });
                playersFilteredArray.push(playersFiltered);
            });
        }
        Object.keys(summary['communityCardsMultiple']).forEach(key => {
            communityCardsArray.push([...summary['communityCards'], ...summary['communityCardsMultiple'][key]]);
        });
    } else {
        if (summary['players']) {
            const players = Object.assign({}, summary['players']);
            const playersFiltered = {};
            Object.keys(players).map((playerId) => {
                const playerStripped = {};
                playerStripped['playerID'] = players[playerId]['playerID'];
                playerStripped['playerName'] = players[playerId]['playerName'];
                playerStripped['profit'] = players[playerId]['profit'];
                playerStripped['hand'] = players[playerId]['hand'];
                playerStripped['folded'] = players[playerId]['folded'];
                playerStripped['shortHandDescription'] = players[playerId]['shortHandDescription'];
                playerStripped['pokerExpressionsDescriptions'] = players[playerId]['pokerExpressionsDescriptions'];
                playerStripped['bestCards'] = players[playerId]['bestCards'];
                playerStripped['handDescription'] = players[playerId]['handDescription'];
                playersFiltered[playerId] = playerStripped;
            });
            playersFilteredArray.push(playersFiltered);
        }
        communityCardsArray.push(summary['communityCards']);
    }
    gameHistorySummary['isMultiple'] = isMultiple;
    gameHistorySummary['players'] = playersFilteredArray;
    gameHistorySummary['communityCards'] = communityCardsArray;
    gameHistorySummary['button'] = summary['button'];
    gameHistorySummary['seats'] = summary['seats'];
    let actions: HandDetailsActions = {...INITIAL_HAND_DETAILS_ACTIONS};

    for (const action of Object.keys(summary['actions'])) {
        let actionsArray: DetailsActionType[] = [];
        for (const actionItem of summary['actions'][action]) {
            if (!summary['players'][actionItem.playerID]) {
                continue;
            }
            const player: DetailsActionPlayer = {
                playerID: actionItem.playerID,
                playerName: summary['players'][actionItem.playerID].playerName,
                positionName: summary['players'][actionItem.playerID].pokerExpressionsDescriptions,
            };
            actionsArray.push({
                action: actionItem.action,
                player: player,
                paid: actionItem['paid'] ? actionItem['paid'] : undefined,
                stack: actionItem['stack'] ? actionItem['stack'] : 0,
                wagerToCall: actionItem['wagerToCall'] ? actionItem['wagerToCall'] : undefined,
                raiseBy: actionItem['raiseBy'] ? actionItem['raiseBy'] : undefined,
                raiseTo: actionItem['raiseTo'] ? actionItem['raiseTo'] : undefined,
                runMultipleAnswer: actionItem['runMultipleAnswer'] ? actionItem['runMultipleAnswer'] : false,
                runMultipleSet: actionItem['runMultipleSet'] ? actionItem['runMultipleSet'] : undefined
            });
        }
        actions[action] = actionsArray;
    }

    let showdownPlayerIds: string[] = [];
    for(let player of Object.values(summary['players'])){
        if(player.actionTracker.wentToShowdown){
            showdownPlayerIds.push(player.playerID);
        }
    }
    let showdownsDetailsArray: ShowdownDetails[][] = [];
    if (isMultiple) {
        if(summary['runMultipleWinnerObjects']) {
            summary['runMultipleWinnerObjects'].forEach(winnerObj => {
                const showdownDetails: ShowdownDetails[] = [];
                showdownPlayerIds.forEach(playerId => {
                    let playerCards: PlayerCard[] = [];
                    winnerObj.players[playerId]['hand'].forEach(card => {
                        playerCards.push({
                            card: card.card,
                            revealed: card.revealed,
                            isRevealRequested: card.isRevealRequested
                        });
                    });
                    const showdownDetail: ShowdownDetails = {
                        playerId: playerId,
                        playerName: winnerObj.players[playerId].name,
                        bestCards: winnerObj.players[playerId]['bestCards'],
                        hand: playerCards,
                        shortHandDescription: winnerObj.players[playerId]['shortHandDescription'],
                        positionName: summary.players[playerId]['pokerExpressionsDescriptions'],
                        profit: winnerObj.winnerObj[playerId][0]
                    };
                    showdownDetails.push(showdownDetail);
                });
                showdownsDetailsArray.push(showdownDetails);
            });
        }
    } else {
        const showdownDetails: ShowdownDetails[] = [];
        for (let playerId of showdownPlayerIds) {
            let playerCards: PlayerCard[] = [];
            for (const card of summary['players'][playerId].hand) {
                playerCards.push({
                   card: card.card,
                   revealed: card.revealed,
                   isRevealRequested: card.isRevealRequested
                });
            }
            const showdownDetail: ShowdownDetails = {
                playerId: playerId,
                playerName: summary['players'][playerId].playerName,
                bestCards: summary['players'][playerId]['bestCards'],
                hand: playerCards,
                shortHandDescription: summary['players'][playerId]['shortHandDescription'],
                positionName: summary['players'][playerId].pokerExpressionsDescriptions,
                profit: summary['players'][playerId].profit
            }
            showdownDetails.push(showdownDetail);
        }
        showdownsDetailsArray.push(showdownDetails);
    }
    const details: HandDetails = {
        actions: actions,
        communityCards: communityCardsArray,
        pots: summary['pots'],
        showdownPlayersArray: showdownsDetailsArray,
    };
    newSummaries[handID] = {
        tableID,
        summary: gameHistorySummary,
        events,
        details,
    };
    const updatedSummaries: IHandHistories = {};
    for (let i = 0; i < newHandIDs.length; i++) {
        if (newSummaries[newHandIDs[i]]) {
            updatedSummaries[newHandIDs[i]] = newSummaries[newHandIDs[i]];
        }
    }
    return updatedSummaries;
}

function addHandID(handIDs: string[], handID: string): string[] {
    const newHandIDs = Object.assign([], handIDs);
    if (newHandIDs.indexOf(handID) < 0) {
        newHandIDs.push(handID);
    }
    const updatedHandIDs = newHandIDs.slice(Math.max(newHandIDs.length - MAX_DISPLAYABLE_HISTORY_COUNT, 0));
    return updatedHandIDs;
}

export function gameHistoryReducer(state: GameHistoryState, action: Action) {
    return _gameHistoryReducer(state, action);
}
